import { useState, FormEvent } from 'react';
import { X, Key, CheckCircle, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { apiClient } from '../services/api';

interface UpdateApiKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
  wabaId: string;
  wabaPhone: string;
  accessToken: string;
  onSuccess: () => void;
}

export default function UpdateApiKeyModal({
  isOpen,
  onClose,
  wabaId,
  wabaPhone,
  accessToken,
  onSuccess,
}: UpdateApiKeyModalProps) {
  const [apiKey, setApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [validationStatus, setValidationStatus] = useState<{
    validated: boolean;
    valid: boolean;
    message: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleValidate = async () => {
    if (!apiKey.trim()) {
      setError('Por favor, informe a API Key');
      return;
    }

    setError(null);
    setIsValidating(true);
    setValidationStatus(null);

    try {
      const response = await apiClient.validateApiKey(apiKey, accessToken);

      if (response.success && response.data) {
        setValidationStatus({
          validated: true,
          valid: response.data.valid,
          message: response.data.message,
        });
      } else {
        setError(response.message || 'Erro ao validar API Key');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao validar API Key');
    } finally {
      setIsValidating(false);
    }
  };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();

    if (!validationStatus?.valid) {
      setError('Por favor, valide a API Key antes de salvar');
      return;
    }

    setError(null);
    setIsSaving(true);

    try {
      const response = await apiClient.updateWabaApiKey(wabaId, apiKey, accessToken);

      if (response.success) {
        onSuccess();
        handleClose();
      } else {
        setError(response.message || 'Erro ao atualizar API Key');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao atualizar API Key');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setApiKey('');
    setShowApiKey(false);
    setValidationStatus(null);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-bg-1 border border-border-1 rounded-lg shadow-xl max-w-md w-full">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold text-text-1">
                Atualizar API Key
              </h2>
              <p className="text-sm text-text-3 mt-1">
                {wabaPhone}
              </p>
            </div>
            <button
              onClick={handleClose}
              className="text-text-3 hover:text-text-2 transition-colors"
              type="button"
            >
              <X size={20} />
            </button>
          </div>

          {/* Info Box */}
          <div className="bg-info/10 border border-accent-1/30 rounded-md p-3 mb-4">
            <p className="text-sm text-text-2">
              A API Key será validada com o Dialog360 antes de ser salva.
              Certifique-se de que a chave está ativa e tem as permissões necessárias.
            </p>
          </div>

          <form onSubmit={handleSave} className="space-y-4">
            {/* API Key Input */}
            <div>
              <label
                htmlFor="apiKey"
                className="flex items-center gap-2 text-sm font-medium text-text-1 mb-2"
              >
                <Key size={16} />
                Nova API Key
              </label>
              <div className="relative">
                <input
                  id="apiKey"
                  type={showApiKey ? 'text' : 'password'}
                  value={apiKey}
                  onChange={(e) => {
                    setApiKey(e.target.value);
                    setValidationStatus(null); // Reset validation when key changes
                  }}
                  className="w-full px-4 py-2 pr-10 bg-bg-2 border border-border-1 rounded-md text-text-1 placeholder:text-text-3 focus:outline-none focus:ring-2 focus:ring-accent-1 focus:border-transparent transition-all font-mono text-sm"
                  placeholder="D360-API-KEY-XXXXXXXXXXXXXX"
                  disabled={isValidating || isSaving}
                />
                <button
                  type="button"
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-text-3 hover:text-text-2 transition-colors"
                >
                  {showApiKey ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Validation Status */}
            {validationStatus && (
              <div
                className={`flex items-center gap-2 p-3 rounded-md ${
                  validationStatus.valid
                    ? 'bg-success/10 border border-success/30 text-success'
                    : 'bg-danger/10 border border-danger/30 text-danger'
                }`}
              >
                {validationStatus.valid ? (
                  <CheckCircle size={18} />
                ) : (
                  <AlertCircle size={18} />
                )}
                <span className="text-sm">{validationStatus.message}</span>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="bg-danger/10 border border-danger/30 rounded-md p-3">
                <p className="text-danger text-sm">{error}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              {!validationStatus?.valid ? (
                <>
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={isValidating}
                    className="flex-1 px-4 py-2 bg-bg-3 hover:bg-bg-3/80 text-text-2 font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={handleValidate}
                    disabled={isValidating || !apiKey.trim()}
                    className="flex-1 px-4 py-2 bg-accent-1 hover:bg-accent-1/90 text-white font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-accent-1"
                  >
                    {isValidating ? 'Validando...' : 'Validar API Key'}
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setValidationStatus(null);
                      setApiKey('');
                    }}
                    disabled={isSaving}
                    className="flex-1 px-4 py-2 bg-bg-3 hover:bg-bg-3/80 text-text-2 font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Alterar
                  </button>
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="flex-1 px-4 py-2 bg-success hover:bg-success/90 text-white font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-success"
                  >
                    {isSaving ? 'Salvando...' : 'Salvar API Key'}
                  </button>
                </>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
